
import { useEffect } from "react";
import { Course, BirdieRecord, TeeBox, User } from "@/types";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import BirdieForm from "@/components/birdie/BirdieForm";
import { useBirdieModal } from "@/hooks/birdieModal/useBirdieModal";
import BirdieHistory from "@/components/birdie/BirdieHistory";
import GameBirdieHistory from "@/components/birdie/GameBirdieHistory";
import { useGameBirdieRecords } from "@/hooks/birdieModal/useGameBirdieRecords";
import { useAuth } from "@/context/AuthContext";

interface RecordBirdieModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  course: Course;
  userId: string;
  selectedHole: number;
  gameId?: string;
  gameIds?: string[];
  gamePlayers?: User[];
  mode?: 'user' | 'game';
}

const RecordBirdieModal = ({
  isOpen,
  onClose,
  onSuccess,
  course,
  userId,
  selectedHole,
  gameId,
  gamePlayers = [],
  mode = 'user',
}: RecordBirdieModalProps) => {
  const { user } = useAuth();
  const {
    loading,
    isEditing,
    editingRecordId,
    editingRecord,
    handleSubmit,
    resetForm,
    handleCancel,
    handleDelete,
    handleEdit,
    birdieRecords,
  } = useBirdieModal({
    courseId: course.id,
    userId,
    selectedHole,
    mode,
  });

  const useGameHistory = mode === 'game' && !!gameId;
  const { records: gameRecords, isLoading: gameHistoryLoading, refreshRecords: refreshGameRecords } = useGameBirdieRecords({
    gameId: useGameHistory ? gameId : undefined,
    courseId: useGameHistory ? course.id : undefined,
    holeNumber: useGameHistory ? selectedHole : null,
  });

  // Filter birdie records for this specific hole (user mode)
  const holeRecords = birdieRecords.filter(record => record.holeNumber === selectedHole);

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      resetForm();
    }
  }, [isOpen, resetForm]);

  const handleCloseAndReset = () => {
    resetForm();
    onClose();
  };

  const handleFormSubmit = async (
    teeBox: TeeBox,
    date: string,
    notes: string,
    relevantGameIds: string[],
    selectedUserId: string,
    gamePlayersMap: Record<string, User[]>,
    presentPlayers: User[],
    scoreType: BirdieRecord['scoreType']
  ) => {
    const success = await handleSubmit(
      selectedHole,
      teeBox,
      date,
      notes,
      relevantGameIds,
      selectedUserId,
      gamePlayersMap,
      presentPlayers,
      scoreType
    );

    if (success) {
      if (useGameHistory) refreshGameRecords();
      onSuccess?.();
      handleCloseAndReset();
    }
  };

  const handleEditRecord = (record: BirdieRecord) => {
    handleEdit(record);
  };

  const selectedHolePar = course.holes.find((h) => h.number === selectedHole)?.par;

  return (
    <Dialog open={isOpen} onOpenChange={handleCloseAndReset}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? "Edit" : "Add"} Score for Hole {selectedHole}
          </DialogTitle>
          {selectedHolePar && (
            <DialogDescription>Par {selectedHolePar}</DialogDescription>
          )}
        </DialogHeader>
        <BirdieForm
          course={course}
          userId={userId}
          existingRecord={editingRecord}
          isLoading={loading}
          gamePlayers={gamePlayers}
          onCancel={() => {
            handleCancel(editingRecordId);
            onClose();
          }}
          onSubmit={handleFormSubmit}
        />

        {/* History: game-scoped or user-scoped */}
        {useGameHistory ? (
          <GameBirdieHistory
            records={gameRecords}
            currentUserId={user?.id || ''}
            courseId={course.id}
            holeNumber={selectedHole}
            onEdit={handleEditRecord}
            onDelete={(record) => {
              handleDelete(record);
              refreshGameRecords();
            }}
            isLoading={gameHistoryLoading}
          />
        ) : (
          <BirdieHistory
            birdieRecords={holeRecords}
            onEdit={handleEditRecord}
            onDelete={handleDelete}
            isLoading={loading}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};

export default RecordBirdieModal;
